/**
 * useAuthenticatedApi Hook
 * 
 * Provides a convenient way to make authenticated API calls with
 * automatic retry logic for handling race conditions during signup.
 * 
 * Usage:
 *   const { callApi, loading, error } = useAuthenticatedApi();
 *   
 *   const handleSubmit = async () => {
 *     const { data, error } = await callApi(async (token) => {
 *       return transferSessionAnswers(token, sessionKey);
 *     });
 *     
 *     if (error) {
 *       // Handle error
 *     } else {
 *       // Success!
 *     }
 *   };
 */

import { useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { callWithAuthRetry } from '../services/auth.service';

// A single shared empty-options reference so callers that don't pass
// options don't trigger ``callApi`` identity churn on every render.
// (Using ``= {}`` as a default created a fresh object per render,
// which broke any consumer that put ``callApi`` in a useEffect dep
// array — the effect would re-fire forever.)
const EMPTY_OPTIONS = Object.freeze({});

export const useAuthenticatedApi = (options = EMPTY_OPTIONS) => {
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  /**
   * Make an authenticated API call with automatic retry on 403 errors.
   * 
   * @param {Function} apiCall - Function that receives token and returns Promise
   * @param {Object} callOptions - Options for this specific call
   * @returns {Promise<{data: any, error: any}>}
   */
  const callApi = useCallback(async (apiCall, callOptions = {}) => {
    if (!isAuthenticated) {
      const authError = { message: 'User is not authenticated' };
      setError(authError);
      return { data: null, error: authError };
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await callWithAuthRetry(
        getAccessTokenSilently,
        apiCall,
        { ...options, ...callOptions }
      );
      
      if (result.error) {
        setError(result.error);
      }
      
      return result;
    } catch (err) {
      const errorObj = { message: err.message };
      setError(errorObj);
      return { data: null, error: errorObj };
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated, options]);
  
  /**
   * Get a fresh access token (forces cache bypass).
   * Useful when you know the token might be stale.
   * 
   * @returns {Promise<string>}
   */
  const getFreshToken = useCallback(async () => {
    return getAccessTokenSilently({ cacheMode: 'off' });
  }, [getAccessTokenSilently]);
  
  /**
   * Get access token (uses cache by default).
   * 
   * @param {boolean} forceRefresh - Force a fresh token
   * @returns {Promise<string>}
   */
  const getToken = useCallback(async (forceRefresh = false) => {
    return getAccessTokenSilently({ cacheMode: forceRefresh ? 'off' : 'on' });
  }, [getAccessTokenSilently]);
  
  return {
    callApi,
    getToken,
    getFreshToken,
    loading,
    error,
    isAuthenticated,
    isAuthLoading: isLoading
  };
};

export default useAuthenticatedApi;
